import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AlertComponent } from "@modules/core/components/alert/alert.component";
import { AnalyticsService } from "@modules/core/services/analytics.service";
import { AuthService } from "@modules/core/services/auth.service";
import { UserService } from "@modules/core/services/user.service";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  defaultIfEmpty,
  filter,
  first,
  fromEvent,
  map,
  of,
  retry,
  shareReplay,
  switchMap,
} from "rxjs";
import { G2ReviewRating } from "types";
import { G2ReviewFeedbackDialogComponent } from "./g2-review-feedback-dialog/g2-review-feedback-dialog.component";
import { G2ReviewFormDialogComponent } from "./g2-review-form-dialog/g2-review-form-dialog.component";
import { G2ReviewPromptDialogComponent } from "./g2-review-prompt-dialog/g2-review-prompt-dialog.component";

/** Matches the server's minimum time between prompts. */
const PROMPT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

const LAST_PROMPTED_STORAGE_KEY_PREFIX = "g2ReviewLastPromptedOnUtc:";

/**
 * The outcome of this page load's eligibility check. `pending` until the check
 * settles; `skipped` when no server check was made (signed out, offline,
 * prompted recently on this device, or another dialog was open).
 */
export type G2ReviewCheckState =
  "pending" | "prompted" | "not-prompted" | "skipped";

type G2ReviewEventType =
  | "G2ReviewPromptView"
  | "G2ReviewRating"
  | "G2ReviewPromptDismiss"
  | "G2ReviewFormView"
  | "G2ReviewFormClose"
  | "G2ReviewFeedbackView"
  | "G2ReviewFeedbackSubmit";

/**
 * Runs the in-app G2 review flow: asks the server whether to prompt, shows
 * the prompt, then routes a positive rating to the G2 review form and a
 * negative rating to internal feedback.
 */
@Injectable({
  providedIn: "root",
})
export class G2ReviewFlowService {
  private hasChecked = false;
  private checkStateSubject = new BehaviorSubject<G2ReviewCheckState>(
    "pending"
  );

  /** Exposed so the app can show when the check has settled. */
  public readonly checkState$ = this.checkStateSubject.asObservable();

  public constructor(
    private matDialog: MatDialog,
    private userService: UserService,
    private authService: AuthService,
    private analyticsService: AnalyticsService
  ) {}

  /**
   * Shows the G2 review prompt if the server says the user is eligible. Call
   * once the user is signed in on a fresh app open. Later calls in the same
   * page load do nothing, so refocusing the app never prompts.
   */
  public promptIfEligible(): void {
    if (this.hasChecked) return;
    this.hasChecked = true;

    const userId = this.authService.getUserId();
    if (!userId || !navigator.onLine || this.wasPromptedRecently(userId)) {
      this.checkStateSubject.next("skipped");
      return;
    }

    this.userService.getG2ReviewPromptState().subscribe({
      next: (state) => {
        if (!state.shouldPrompt) {
          this.checkStateSubject.next("not-prompted");
          return;
        }
        // Only show, record and count the prompt once the user can see it.
        this.whenPageVisible().subscribe(() => {
          // Never stack the prompt on top of another dialog.
          if (this.matDialog.openDialogs.length > 0) {
            this.checkStateSubject.next("skipped");
            return;
          }
          this.showPrompt(userId, (state.promptCount ?? 0) + 1);
          this.checkStateSubject.next("prompted");
        });
      },
      error: () => this.checkStateSubject.next("not-prompted"),
    });
  }

  private whenPageVisible(): Observable<void> {
    if (document.visibilityState === "visible") return of(undefined);
    return fromEvent(document, "visibilitychange").pipe(
      filter(() => document.visibilityState === "visible"),
      first(),
      map(() => undefined)
    );
  }

  private showPrompt(userId: string, promptCount: number): void {
    // The server only accepts a rating within 24 hours of a recorded display,
    // so a rating must wait for this call to finish, whether or not it worked.
    const displayRecorded = this.userService.recordG2ReviewPromptShown().pipe(
      retry(1),
      catchError(() => of(undefined)),
      defaultIfEmpty(undefined),
      map(() => undefined),
      shareReplay(1)
    );

    const promptRef = G2ReviewPromptDialogComponent.openDialog(this.matDialog, {
      submitRating: (rating) => {
        this.logEvent("G2ReviewRating", { rating });
        return displayRecorded.pipe(
          switchMap(() => this.userService.recordG2ReviewRating(rating))
        );
      },
    });

    this.setLastPrompted(userId, new Date());
    displayRecorded.subscribe();
    this.logEvent("G2ReviewPromptView", { promptCount });

    promptRef.afterClosed().subscribe((result) => {
      if (!result) {
        this.logEvent("G2ReviewPromptDismiss");
        return;
      }
      // A rejected rating has already shown an error in the prompt.
      if (result.outcome === "rejected") return;

      if (result.rating === "negative") {
        this.openFeedback();
      } else if (result.reviewUrl) {
        this.openReviewForm(result.reviewUrl);
      }
    });
  }

  private openReviewForm(reviewUrl: string): void {
    const formRef = G2ReviewFormDialogComponent.openDialog(this.matDialog, {
      reviewUrl,
    });

    formRef.componentInstance.formLoaded.subscribe(() =>
      this.logEvent("G2ReviewFormView")
    );
    formRef.afterClosed().subscribe(() => this.logEvent("G2ReviewFormClose"));
  }

  private openFeedback(): void {
    const feedbackRef = G2ReviewFeedbackDialogComponent.openDialog(
      this.matDialog,
      {
        submitFeedback: (feedbackText) => {
          this.logEvent("G2ReviewFeedbackSubmit");
          return this.userService.submitG2ReviewFeedback({ feedbackText });
        },
      }
    );
    this.logEvent("G2ReviewFeedbackView");

    feedbackRef.afterClosed().subscribe((sent) => {
      if (sent) this.openThanks();
    });
  }

  private openThanks(): void {
    AlertComponent.openDialog(this.matDialog, {
      title: "Thanks for your feedback",
      message: "The team reads every response.",
      acceptButtonText: "Close",
    });
  }

  private logEvent(
    eventType: G2ReviewEventType,
    fields: { promptCount?: number; rating?: G2ReviewRating } = {}
  ): void {
    this.analyticsService
      .logEvent({ eventType, timestamp: new Date().toISOString(), ...fields })
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  /**
   * A local backstop for the server's 30-day rule, in case the prompt was
   * shown but recording the display failed.
   */
  private wasPromptedRecently(userId: string): boolean {
    try {
      const value = localStorage.getItem(
        LAST_PROMPTED_STORAGE_KEY_PREFIX + userId
      );
      if (!value) return false;
      const lastPrompted = new Date(value).getTime();
      return Date.now() - lastPrompted < PROMPT_COOLDOWN_MS;
    } catch {
      return false;
    }
  }

  private setLastPrompted(userId: string, date: Date): void {
    try {
      localStorage.setItem(
        LAST_PROMPTED_STORAGE_KEY_PREFIX + userId,
        date.toISOString()
      );
    } catch {
      // Storage can be unavailable; the server rule still applies.
    }
  }
}
